import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { Check, Calendar, Mail, User as UserIcon, CreditCard } from 'lucide-react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  if (!user) {
    navigate('/');
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pb-24 md:pb-12"
      data-testid="profile-page"
    >
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="font-serif text-5xl md:text-6xl text-charcoal tracking-tighter mb-4">
                Mon Profil
              </h1>
              <p className="text-lg text-taupe">Gérez vos informations et votre abonnement</p>
            </div>

            {/* Profile Card */}
            <div className="bg-silk p-8 md:p-12 border gold-border mb-8">
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-primary flex items-center justify-center">
                    <UserIcon className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="font-serif text-3xl text-charcoal tracking-tighter mb-2" data-testid="profile-name">
                      {user.name}
                    </h2>
                    <p className="text-taupe" data-testid="profile-email">{user.email}</p>
                  </div>
                </div>

                {user.has_subscription && (
                  <div className="bg-primary px-4 py-2">
                    <span className="font-accent text-xs text-white uppercase tracking-wider">
                      Premium
                    </span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-8 border-t gold-border">
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="text-sm text-taupe uppercase tracking-wide mb-1">Email</p>
                    <p className="text-charcoal">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Calendar className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <p className="text-sm text-taupe uppercase tracking-wide mb-1">Membre depuis</p>
                    <p className="text-charcoal" data-testid="profile-member-since">{formatDate(user.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Status */}
            <div className="bg-porcelain p-8 md:p-12 border gold-border mb-8">
              <h3 className="font-serif text-3xl text-charcoal tracking-tighter mb-6">
                Statut de l'abonnement
              </h3>

              {user.has_subscription ? (
                <div data-testid="subscription-active">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-primary flex items-center justify-center">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-serif text-2xl text-charcoal">Abonnement Actif</p>
                      <p className="text-taupe">Accès illimité à vie</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t gold-border">
                    <div className="flex items-start gap-4">
                      <Calendar className="w-5 h-5 text-primary mt-1" />
                      <div>
                        <p className="text-sm text-taupe uppercase tracking-wide mb-1">Date d'activation</p>
                        <p className="text-charcoal" data-testid="subscription-date">
                          {formatDate(user.subscription_date)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <CreditCard className="w-5 h-5 text-primary mt-1" />
                      <div>
                        <p className="text-sm text-taupe uppercase tracking-wide mb-1">Type</p>
                        <p className="text-charcoal">Lifetime Premium - $9.29</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-6 bg-primary/5">
                    <p className="text-sm text-charcoal">
                      ✨ Vous avez un accès illimité à tous nos tutoriels premium. 
                      Profitez du nouveau contenu ajouté chaque semaine!
                    </p>
                  </div>
                </div>
              ) : (
                <div data-testid="subscription-inactive">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 border gold-border flex items-center justify-center">
                      <X className="w-6 h-6 text-taupe" />
                    </div>
                    <div>
                      <p className="font-serif text-2xl text-charcoal">Aucun abonnement actif</p>
                      <p className="text-taupe">Abonnez-vous pour accéder au contenu premium</p>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/subscription')}
                    data-testid="profile-subscribe-btn"
                    className="bg-secondary text-white hover:bg-secondary/90 px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500"
                  >
                    S'abonner maintenant - $9.29
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={logout}
                data-testid="profile-logout-btn"
                className="flex-1 bg-transparent border gold-border text-charcoal hover:bg-charcoal hover:text-white px-8 py-4 uppercase tracking-widest font-serif text-sm transition-all duration-500"
              >
                Se déconnecter
              </button>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

import { X } from 'lucide-react';
export default ProfilePage;